/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Button,
  Card,
  InputNumber,
  Input,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import { formatReceivedValues } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import {
  getVacacionesTiposNominasEmpleados,
  getVacacionesTipoNominaEmpleado,
  postVacacionesTipoNominaEmpleado,
  patchVacacionesTipoNominaEmpleado,
  deleteVacacionesTipoNominaEmpleado,
} from 'api/nominas/vacaciones/vacacionesTiposNominasEmpleados';
import { getTiposDeNominasEmpleados } from 'api/nominas/catalogos/tiposNominasEmpleados';
import {
  ESTATUS_ACTIVO,
  ESTATUS_CANCELADO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
} from '../../../utils/constants';

// Components
import VacacionesConfiguracionTable from './VacacionesConfiguracionesTable';

export const vacacionesConfiguracionPermission = {
  permissionModel: 'vacaciontipodenominaempleado',
  catalogsModels: [],
  tabsModels: [],
};

// eslint-disable-next-line react/prop-types
const VacacionesConfiguracion = ({ permission }) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [estados, setEstados] = useState();
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [tiposNominasEmpleados, setTiposNominasEmpleados] = useState([]);
  const [tipoNominaEmpleadoFiltered, setTipoNominaEmpleadoFiltered] = useState([]);
  const [selectedTipoNominaEmpleado, setSelectedTipoNominaEmpleado] = useState();
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [confirmationVisibleEstatus, setConfirmationVisibleEstatus] = useState(false);
  const [estatusInactivo, setEstatusInactivo] = useState(false);
  const [estatusAutorizado, setEstatusAutorizado] = useState(false);
  const [estadosFiltered, setEstadosFiltered] = useState([]);
  const [disabledSave, setDisabledSave] = useState(false);

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getVacacionesTiposNominasEmpleados();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tipos_nominas_empleados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeNominasEmpleados({ estados_globales: ESTATUS_AUTORIZADO }),
        getVacacionesTiposNominasEmpleados(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTiposNominasEmpleados(_tipos_nominas_empleados);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setEstatusInactivo(false);
    setDisabledSave(false);
    setEstatusAutorizado(false);
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickAdd = () => {
    setEstadosFiltered(estados);
    onCancel();
    setTipoNominaEmpleadoFiltered(tiposNominasEmpleados.filter((tne) => !data.some((d) => {
      if (d?.tipo_de_nomina_empleado === tne.id) {
        return true;
      }
      return false;
    })));
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisabledSave(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _vacaciones = await getVacacionesTipoNominaEmpleado(match.id);
    if (_vacaciones?.estados_globales === ESTATUS_ACTIVO
      || _vacaciones?.estados_globales === ESTATUS_INACTIVO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_INACTIVO
        || estado?.id === ESTATUS_ACTIVO));
    }
    if (_vacaciones?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_AUTORIZADO));
    }
    if (_vacaciones?.estados_globales === ESTATUS_INACTIVO) {
      setEstatusInactivo(true);
    }
    if (_vacaciones?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstatusAutorizado(true);
    }
    setTimeout(() => {
      if (_vacaciones) {
        form.resetFields();
        setTimeout(() => {
          isDisabled(_vacaciones?.estados_globales);
          setSelectedTipoNominaEmpleado(_vacaciones?.tipo_de_nomina_empleado);
          setTipoNominaEmpleadoFiltered(tiposNominasEmpleados.filter((tne) => !data.some((d) => {
            if (_vacaciones?.tipo_de_nomina_empleado === tne?.id) {
              return false;
            }
            if (d?.tipo_de_nomina_empleado === tne?.id) {
              return true;
            }
            return false;
          })));
          form.setFieldsValue(formatReceivedClone(_vacaciones));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchVacacionesTipoNominaEmpleado(key, values) : postVacacionesTipoNominaEmpleado(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteVacacionesTipoNominaEmpleado(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una nómina ya autorizada');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchVacacionesTipoNominaEmpleado(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisible(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    setConfirmationVisibleEstatus(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedTipoNominaEmpleado(record.tipo_de_nomina_empleado);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  const columns = [
    {
      titleText: 'Tipo de Nómina Empleado',
      dataIndex: 'tipo_de_nomina_empleado',
      key: 'tipo_de_nomina_empleado',
      width: 300,
      render: (val) => tiposNominasEmpleados.find((tne) => tne?.id === val)?.descripcion,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            data={data}
            cols={columns}
            allowExportParam={false}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            disableDelete={
              data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
            }
            customActions={[
              {
                onClick: () => setConfirmationVisible(true),
                permission: { model: 'vacaciontipodenominaempleado', action: 'autorizar' },
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'success',
                disabled: !selectedRowKeys?.length
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
              },
            ]}
            permission={permission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Configuración de Vacaciones`}
            extra={(
              <FormSubmitControls
                onFinish={() => form.submit()}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar"
                allowSave={!disabledSave}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="general"
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="tipo_de_nomina_empleado"
                    label="Tipo de Nómina Empleado"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      dataSource={tipoNominaEmpleadoFiltered}
                      disabled={estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="antiguedad_minima_primeras_vacaciones"
                    label="Antigüedad Mínima para Primeras Vacaciones"
                    rules={rules.required}
                    hasFeedback
                  >
                    <InputNumber
                      min={1}
                      max={12} // numeric limit
                      style={{ width: '100%' }}
                      disabled={estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={2}>
                  <Form.Item
                    label=" "
                    name="meses"
                    initialValue="Meses"
                    hasFeedback
                  >
                    <Input
                      disabled

                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item
                    name="dias_minimos_primeras_vacaciones"
                    label="Días Mínimos para Primeras Vacaciones"
                    rules={rules.required}
                    hasFeedback
                  >
                    <InputNumber
                      min={1}
                      max={9999} // numeric limit
                      disabled={estatusAutorizado || estatusInactivo}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={2}>
                  <Form.Item
                    label=" "
                    name="dias"
                    initialValue="Días"
                    hasFeedback
                  >
                    <Input
                      disabled

                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="aplica_primeras_vacaciones"
                    label="Aplica Guardias para Vacaciones"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      trueFalse
                      disabled={estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="aplica_guardias_antiguedad_menor_un_anio"
                    label="Aplica Guardias para Antigüedad Menor a 1 año"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      trueFalse
                      disabled={estatusAutorizado || estatusInactivo}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="estados_globales"
                    label="Estatus"
                    rules={rules.estatus}
                    hasFeedback
                    initialValue={1}
                  >
                    <Select
                      labelProp="descripcion"
                      keyProp="id"
                      dataSource={estadosFiltered}
                      disabled={!selectedRowKeys.length
                        || estatusAutorizado || estatusInactivo}
                      onChange={(val) => {
                        if (val === ESTATUS_INACTIVO) {
                          setConfirmationVisibleEstatus(true);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
            {/* {selectedRowKeys.length ? (
              <VacacionesConfiguracionTable
                permission={permission}
                selectedTipoNominaEmpleado={selectedTipoNominaEmpleado}
                selectedRowParent={selectedRowKeys[0]}
                tiposNominasEmpleados={tiposNominasEmpleados}
                estatusAutorizado={estatusAutorizado}
                estatusInactivo={estatusInactivo}
              />
            ) : null} */}
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Configuración de Vacaciones ${tiposNominasEmpleados.find((tne) => tne?.id === form.getFieldValue('tipo_de_nomina_empleado'))?.descripcion}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`Autorizar la Configuración de Vacaciones para ${tiposNominasEmpleados.find((tne) => tne?.id === form.getFieldValue('tipo_de_nomina_empleado'))?.descripcion}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          ConfirmInactive
          onCancel={() => {
            setConfirmationVisibleEstatus(false);
            form.setFieldValue('estados_globales', 1);
          }}
          open={confirmationVisibleEstatus}
          contentTitle={`Inactivar la Configuración de Vacaciones para ${tiposNominasEmpleados.find((tne) => tne?.id === form.getFieldValue('tipo_de_nomina_empleado'))?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default VacacionesConfiguracion;
